<!-- 
Panel for entering levy calculation parameters into the levy calculator playground
-->
<template>
  <v-form ref="form">
    <v-card :elevation="forPublicEstimator ? '0' : undefined">
      <slot name="toolbar" />
      <v-card-text class="mt-0 pt-0">
        <v-container grid-list-md pa-0>
          <v-layout wrap mx-4>
            <v-flex xs12 pt-4>
              <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
              <div v-if="forPublicEstimator" v-html="templates.PublicEstimatorParameters"/>
              <!-- eslint-disable-next-line vue/no-v-text-v-html-on-component vue/no-v-html -->
              <div v-else v-html="templates.LevyEstimatorPlayground"/>
            </v-flex>

            <!-- Loans -->
            <template v-if="!levyParameters.isNew">
              <v-flex xs12>
                <playground-subheading :help="templates.LevyEstimatorPlaygroundLoans">{{loanTypeHeading}}</playground-subheading>
              </v-flex>
              <v-flex xs12 md4 sm6>
                <our-currency-input 
                  :value="levyParameters.loanAmountsPaid"
                  :label="`Total ${levyCollectionTypeDescription} ${loanTypeDescription} in ${levyCollectionYear - 1}`" 
                  :hint="getHint('loanAmountsPaid', 'currency')"
                  :append-icon="getAppendIcon('loanAmountsPaid')"
                  autofocus
                  persistent-hint
                  max="1000000000000"
                  required
                  @click:append="revert('loanAmountsPaid')"
                  @input="changed('loanAmountsPaid', $event)"
                />
              </v-flex>
            </template>

            <!-- Student count -->
            <template v-if="!levyParameters.isNew">
              <v-flex xs12>
                <playground-subheading :help="templates.LevyEstimatorPlaygroundStudentCounts">Students</playground-subheading>
              </v-flex>
              <v-flex xs12 md4 sm6>
                <v-text-field 
                  :value="levyParameters.studentCount"
                  :rules="[rules.required,rules.integer,rules.zeroOrGreater,rules.sensibleNumber]"
                  :label="`${levyCollectionTypeDescription} students in ${levyCollectionYear - 1}`" 
                  :hint="getHint('studentCount')"
                  :append-icon="getAppendIcon('studentCount')"
                  persistent-hint
                  @click:append="revert('studentCount')"
                  @input="changed('studentCount', $event)"
                />
              </v-flex>
            </template>

            <!-- Financials -->
            <template v-if="!levyParameters.riskRatingExempt && !levyParameters.isNew">
              <v-flex xs12>
                <playground-subheading :help="templates.LevyEstimatorPlaygroundFinancials">Financials</playground-subheading>
              </v-flex>
              <v-flex xs12 md4 sm6>
                <v-select
                  :value="levyParameters.financialStatementSupplied" 
                  :items="lookups.yesNo"
                  :hint="getHint('financialStatementSupplied', 'yesNo')"
                  :append-icon="getAppendIcon('financialStatementSupplied')"
                  label="Financial statement supplied"
                  persistent-hint
                  autofocus
                  @click:append="revert('financialStatementSupplied')"
                  @input="changed('financialStatementSupplied', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <template v-if="levyParameters.financialStatementSupplied">
                <v-flex xs12 md4 sm6>
                  <our-currency-input
                    :value="levyParameters.netProfitBeforeTax"
                    :hint="getHint('netProfitBeforeTax', 'currency')"
                    :append-icon="getAppendIcon('netProfitBeforeTax')"
                    label="Net profit before tax" 
                    required
                    persistent-hint
                    @click:append="revert('netProfitBeforeTax')"
                    @input="changed('netProfitBeforeTax', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <our-currency-input
                    :value="levyParameters.totalRevenue"
                    :hint="getHint('totalRevenue', 'currency')"
                    :append-icon="getAppendIcon('totalRevenue')"
                    label="Total revenue" 
                    required
                    zero-or-greater
                    persistent-hint
                    @click:append="revert('totalRevenue')"
                    @input="changed('totalRevenue', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <our-currency-input
                    :value="levyParameters.totalAssets"
                    :hint="getHint('totalAssets', 'currency')"
                    :append-icon="getAppendIcon('totalAssets')"
                    label="Total assets" 
                    required
                    zero-or-greater
                    persistent-hint
                    @click:append="revert('totalAssets')"
                    @input="changed('totalAssets', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <our-currency-input
                    :value="levyParameters.totalLiabilities"
                    :hint="getHint('totalLiabilities', 'currency')"
                    :append-icon="getAppendIcon('totalLiabilities')"
                    label="Total liabilities" 
                    required
                    zero-or-greater
                    persistent-hint
                    @click:append="revert('totalLiabilities')"
                    @input="changed('totalLiabilities', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <our-currency-input
                    :value="levyParameters.totalEquity"
                    hint="Automatically calculated as total assets - total liabilities"
                    label="Total equity" 
                    readonly
                  />
                </v-flex>
              </template>
            </template>

            <!-- EFTSL -->
            <template v-if="!levyParameters.isNew">
              <template v-if="!levyParameters.riskRatingExempt">
                <v-flex xs12>
                  <playground-subheading :help="templates.LevyEstimatorPlaygroundEftsl">Equivalent full-time student load</playground-subheading>
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <v-text-field 
                    :value="levyParameters.passedEftsl"
                    :rules="[rules.required,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint('passedEftsl')"
                    :append-icon="getAppendIcon('passedEftsl')"
                    :label="`Passed EFTSL (${lastYear})`"
                    persistent-hint 
                    @click:append="revert('passedEftsl')"
                    @input="changed('passedEftsl', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <v-text-field 
                    :value="levyParameters.withdrawnEftsl"
                    :rules="[rules.required,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint('withdrawnEftsl')"
                    :append-icon="getAppendIcon('withdrawnEftsl')"
                    :label="`Withdrawn EFTSL (${lastYear})`"
                    persistent-hint 
                    @click:append="revert('withdrawnEftsl')"
                    @input="changed('withdrawnEftsl', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <v-text-field 
                    :value="levyParameters.failedEftsl"
                    :rules="[rules.required,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint('failedEftsl')"
                    :append-icon="getAppendIcon('failedEftsl')"
                    :label="`Failed EFTSL (${lastYear})`"
                    persistent-hint 
                    @click:append="revert('failedEftsl')"
                    @input="changed('failedEftsl', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <v-text-field 
                    :value="levyParameters.ongoingEftsl"
                    :rules="[rules.required,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint('ongoingEftsl')"
                    :append-icon="getAppendIcon('ongoingEftsl')"
                    :label="`Ongoing EFTSL (${lastYear})`"
                    persistent-hint 
                    @click:append="revert('ongoingEftsl')"
                    @input="changed('ongoingEftsl', $event)"
                  />
                </v-flex>
                <v-flex xs12 md4 sm6>
                  <v-text-field 
                    :value="levyParameters.missingEftsl"
                    :rules="[rules.required,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint('missingEftsl')"
                    :append-icon="getAppendIcon('missingEftsl')"
                    :label="`Missing EFTSL (${lastYear})`"
                    persistent-hint 
                    @click:append="revert('missingEftsl')"
                    @input="changed('missingEftsl', $event)"
                  />
                </v-flex>
              </template>
            </template>

            <!-- Non-compliance history -->
            <template v-if="showNonCompliance">
              <v-flex xs12>
                <playground-subheading :help="templates.LevyEstimatorPlaygroundNonComplianceHistory">{{ nonComplianceSubHeadingLabel }}</playground-subheading>
              </v-flex>
              <v-flex v-if="enableSanctions" xs12 md4 sm6>
                <v-select
                  :value="levyParameters.sanctioned" 
                  :items="lookups.yesNo"
                  :hint="getHint('sanctioned', 'yesNo')"
                  :append-icon="getAppendIcon('sanctioned')"
                  label="Sanctioned"
                  persistent-hint
                  autofocus
                  @click:append="revert('sanctioned')"
                  @input="changed('sanctioned', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
              <template v-if="!levyParameters.sanctioned || !enableSanctions">
                <v-flex 
                  v-for="y in ltpsLatePaymentYears" 
                  :key="y.field" 
                  xs12 md4 sm6
                >
                  <v-text-field 
                    :label="y.label" 
                    :value="y.value"
                    :rules="[rules.integer,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint(y.field)"
                    :append-icon="getAppendIcon(y.field)"
                    persistent-hint
                    @click:append="revert(y.field)"
                    @input="changed(y.field, $event)"
                  />
                </v-flex>
                <v-flex 
                  v-for="y in annualChargeLatePaymentYears"
                  :key="y.field"
                  xs12 md4 sm6
                >
                  <v-text-field 
                    :label="y.label" 
                    :value="y.value"
                    :rules="[rules.integer,rules.zeroOrGreater,rules.sensibleNumber]"
                    :hint="getHint(y.field)"
                    :append-icon="getAppendIcon(y.field)"
                    persistent-hint
                    @click:append="revert(y.field)"
                    @input="changed(y.field, $event)"
                  />
                </v-flex>
              </template>
              <v-flex v-if="enableShortRegistrationPeriodRiskFactor" xs12 md4 sm6>
                <v-select 
                  :items="lookups.booleanTrueFalse"
                  :value="levyParameters.shortRegistrationPeriod" 
                  :hint="getHint('shortRegistrationPeriod', 'trueFalse')"
                  :append-icon="getAppendIcon('shortRegistrationPeriod')"
                  label="Short registration renewal period"
                  persistent-hint
                  autofocus
                  @click:append="revert('shortRegistrationPeriod')"
                  @input="changed('shortRegistrationPeriod', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
            </template> 

            <!-- Special cases -->
            <v-flex xs12>
              <playground-subheading :help="templates.LevyEstimatorPlaygroundSpecialCases">Special cases</playground-subheading>
            </v-flex>
            <v-flex xs12 md4 sm6>
              <v-select
                :value="levyParameters.isNew" 
                :items="lookups.yesNo"
                :label="`New ${levyCollectionTypeDescription} provider for ${levyCollectionYear}`"
                :hint="getHint('isNew', 'yesNo')"
                :append-icon="getAppendIcon('isNew')"
                persistent-hint
                autofocus
                @click:append="revert('isNew')"
                @input="changed('isNew', $event)"
                @keydown.enter.native.prevent
              />
            </v-flex>
            <template v-if="!levyParameters.isNew">
              <v-flex v-if="showIsLiableForLastYearsAdministrativeFee" xs12 md4 sm6>
                <v-select
                  :value="levyParameters.isLiableForLastYearsAdministrativeFee" 
                  :items="lookups.yesNo"
                  :hint="getHint('isLiableForLastYearsAdministrativeFee', 'yesNo')"
                  :append-icon="getAppendIcon('isLiableForLastYearsAdministrativeFee')"
                  persistent-hint
                  label="Is liable for last year's administrative fee"
                  @click:append="revert('isLiableForLastYearsAdministrativeFee')"
                  @input="changed('isLiableForLastYearsAdministrativeFee', $event)"
                  @keydown.enter.native.prevent
                />
              </v-flex>
            </template>
          </v-layout>
          <p v-if="hasOverride" class="mx-4 mt-4 mb-0 pb-0 font-weight-bold">
            Note: at least one component of your levy estimate calculation was overridden. 
            These overrides will not be incorporated into the levy estimator's calculations.
          </p>
        </v-container>
      </v-card-text>
      <v-card-actions v-if="forPublicEstimator" class="justify-center">
        <v-btn 
          :loading="buttonsDisabled"
          color="primary" 
          type="submit" 
          large
          @click.prevent="recalculate"
        >
          Calculate
        </v-btn>
      </v-card-actions>
      <v-card-actions v-else>
        <v-btn :disabled="buttonsDisabled" flat color="error" @click="$emit('click:reset')">Reset</v-btn>
        <v-spacer/>
        <v-btn :disabled="buttonsDisabled" flat @click="$emit('click:cancel')">Cancel</v-btn>
        <v-btn 
          :disabled="buttonsDisabled" 
          flat 
          color="primary" 
          type="submit" 
          @click.prevent="recalculate"
        >
          Recalculate
        </v-btn>
      </v-card-actions>
    </v-card>
    <our-validation-dialog v-model="showValidationDialog" :form="this.$refs.form" :specified-error="specifiedError" />
  </v-form>
</template>

<script>
import _ from "underscore";
import PlaygroundSubheading from "./PlaygroundSubheading.vue";
import filterMixins from "@/mixins/filter.mixins";
import lookupMixins from "@/mixins/lookup.mixins";
import ruleMixins from "@/mixins/rule.mixins";
import * as formatting from "@/helpers/formatting.helper";
import * as mutation from "@/store/levy-estimator/mutation-types";
import { getBlankRecord } from "@/store/levy-collection-overrides/helpers";

export default {
  components: { PlaygroundSubheading },
  mixins: [filterMixins, lookupMixins, ruleMixins],
  props: {
    levyCollection: {
      type: Object,
      default: null
    },
    buttonsDisabled: {
      type: Boolean,
      default: false
    },
    forPublicEstimator: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      showValidationDialog: false,
      specifiedError: null,
      rules: {
        max99: v => (v && v > 99 ? "Value must be less than 100" : true),
        sensibleNumber: v => (v && v >= 10000000 ? "Value is too big" : true)
      }
    };
  },
  computed: {
    state() {
      return this.forPublicEstimator
        ? this.$store.state.levyEstimator.publicEstimator
        : this.$store.state.levyEstimator.playground;
    },
    levyCollectionYear() {
      return this.levyCollection ? this.levyCollection.year : 0;
    },
    levyCollectionType() {
      return this.levyCollection ? this.levyCollection.type : "";
    },
    levyCollectionTypeDescription() {
      return this.levyCollection ? this.lookups.levyCollectionTypeDescriptions[this.levyCollection.type] : "";
    },
    loanTypeHeading() {
      return this.upfront ? "Payments" : "Loans";
    },
    loanTypeDescription() {
      return this.upfront ? "payments" : "loans";
    },
    enableSanctions() {
      return this.levyCollection ? this.levyCollection.enableSanctions : false;
    },
    enableShortRegistrationPeriodRiskFactor() {
      return this.levyCollection ? this.levyCollection.enableShortRegistrationPeriodRiskFactor : false;
    },
    templates() {
      return this.state.data.indexedTemplates ? this.state.data.indexedTemplates : {};
    },
    levyParameters() {
      return this.state.edit;
    },
    originalLevyParameters() {
      return this.forPublicEstimator
        ? null
        : this.$store.state.levyEstimator.estimator.data.levyCollectionProvider.levyParameters;
    },
    vsl() {
      return this.levyCollectionType.toLowerCase() == "vsl";
    },
    help() {
      return this.levyCollectionType.toLowerCase() == "help";
    },
    upfront() {
      return this.levyCollectionType.toLowerCase() == "upfront";
    },
    lastYear() {
      return this.levyCollectionYear - 1;
    },
    hasOverride() {
      if (this.forPublicEstimator) return false;
      let override = _(this.$store.state.levyEstimator.estimator.data.levyCollectionProvider.overrides).find(
        o => o.category == "AdjustLevyCalculations"
      );
      override = override ? override : getBlankRecord();
      return (
        override.hasAnyOverrides &&
        (override.administrativeFeeOverrideType != null ||
          override.riskPremiumOverrideType != null ||
          override.specialTuitionProtectionOverrideType != null)
      );
    },
    showIsLiableForLastYearsAdministrativeFee() {
      return this.levyCollectionYear > 2020;
    },
    ltpsLevyLabel() {
      return this.vsl
        ? "VSL levy days late"
        : this.help
          ? "HELP levy days late"
          : this.upfront
            ? "Up-front levy days late"
            : "UNKNOWN levy days late";
    },
    annualChargeLabel() {
      return this.vsl
        ? "VSL annual charge days late"
        : this.help
          ? "HELP annual charge days late"
          : this.upfront
            ? "Up-front annual charge days late"
            : "UNKNOWN annual charge days late";
    },
    showNonCompliance() {
      const hasInputs =
        this.ltpsLatePaymentYears.length > 0 ||
        this.annualChargeLatePaymentYears.length > 0 ||
        this.enableSanctions ||
        this.enableShortRegistrationPeriodRiskFactor;
      return hasInputs && !this.levyParameters.riskRatingExempt && !this.levyParameters.isNew;
    },
    nonComplianceSubHeadingLabel() {
      return this.enableShortRegistrationPeriodRiskFactor
        ? "Non-compliance history and registration renewal"
        : "Non-compliance history";
    },
    ltpsLatePaymentYears() {
      const years = [];
      const year = this.levyCollectionYear;
      const count = this.levyCollection ? this.levyCollection.numberOfLtpsLatePaymentYears : 0;
      for (let i = 1; i <= count; i++) {
        const field =
          i == 1 ? "ltpsLevyLatePaymentDaysInPreviousYear" : `ltpsLevyLatePaymentDaysInPreviousYearMinus${i - 1}`;
        years.push({
          label: `${this.ltpsLevyLabel} (${year - i})`,
          value: this.levyParameters[field],
          field: field
        });
      }
      return years;
    },
    annualChargeLatePaymentYears() {
      const years = [];
      const year = this.levyCollectionYear;
      const count = this.levyCollection ? this.levyCollection.numberOfAnnualChargeLatePaymentYears : 0;
      for (let i = 1; i <= count; i++) {
        const field =
          i == 1
            ? "annualChargeLatePaymentDaysInPreviousYear"
            : `annualChargeLatePaymentDaysInPreviousYearMinus${i - 1}`;
        years.push({
          label: `${this.annualChargeLabel} (${year - i})`,
          value: this.levyParameters[field],
          field: field
        });
      }
      return years;
    }
  },
  methods: {
    changed(field, value) {
      const m = this.forPublicEstimator ? mutation.PUBLIC_ESTIMATOR_UPDATE : mutation.PLAYGROUND_EDIT_UPDATE;
      if (field == "isNew" && value) {
        this.$store.commit(m, { isNew: true, administrationFeeExempt: false });
      } else {
        this.$store.commit(m, { [field]: value });
        const assets = this.levyParameters.totalAssets;
        const liabilities = this.levyParameters.totalLiabilities;
        if (isNaN(assets) || isNaN(liabilities)) {
          this.$store.commit(m, { totalEquity: "" });
        } else {
          this.$store.commit(m, { totalEquity: formatting.round(assets - liabilities, 2) });
        }
      }
    },
    getHint(field, format) {
      if (this.forPublicEstimator) return null;
      const original = this.originalLevyParameters[field];
      if (original != null) {
        const formattedOriginal = format ? formatting[format](original) : original;
        if (this.levyParameters[field] != original) {
          return `Originally ${formattedOriginal}`;
        }
      }
      return null;
    },
    getAppendIcon(field) {
      if (this.forPublicEstimator) return null;
      const isDifferent = this.originalLevyParameters[field] != this.levyParameters[field];
      return isDifferent ? "undo" : undefined;
    },
    revert(field) {
      this.changed(field, this.originalLevyParameters[field]);
    },
    recalculate() {
      if (this.$refs.form.validate()) {
        this.$emit("click:recalculate");
      } else {
        this.showValidationDialog = true;
      }
    }
  }
};
</script>
